import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { MAIN_NAV } from '@/constants/navigation'
import { siteConfig } from '@/constants/site'

interface MobileMenuProps {
    open: boolean
    onClose: () => void
}

export const MobileMenu: FC<MobileMenuProps> = ({ open, onClose }): ReactNode => {
    const panelRef = useRef<HTMLDivElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!open) return

        const root = document.documentElement
        const previousOverflow = root.style.overflow

        root.style.overflow = 'hidden'
        closeRef.current?.focus()

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                onClose()

                return
            }

            if (event.key !== 'Tab' || !panelRef.current) return

            const focusable = panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')

            if (!focusable.length) return

            const first = focusable[0]
            const last = focusable[focusable.length - 1]

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', onKeyDown)

        return () => {
            root.style.overflow = previousOverflow
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [open, onClose])

    return (
        <div
            id='mobile-menu'
            ref={panelRef}
            role='dialog'
            aria-modal='true'
            aria-label='Site menu'
            inert={!open}
            className={cn(
                'fixed inset-0 z-[60] flex flex-col bg-burgundy text-cream transition-[opacity,visibility] duration-500 ease-out lg:hidden',
                open ? 'visible opacity-100' : 'invisible opacity-0'
            )}
        >
            <div className='flex h-16 shrink-0 items-center justify-between px-5 sm:px-8'>
                <Link href='/' onClick={onClose} aria-label='Veyra, back to home' className='rounded-md'>
                    <Logo tone='light' />
                </Link>

                <button
                    ref={closeRef}
                    type='button'
                    onClick={onClose}
                    className='inline-flex h-10 items-center gap-2 rounded-full border border-cream/20 pl-4 pr-3 text-sm font-medium transition-colors hover:border-cream/40'
                >
                    Close
                    <X aria-hidden='true' className='size-4' />
                </button>
            </div>

            <nav aria-label='Mobile' className='flex flex-1 flex-col justify-center overflow-y-auto px-5 py-8 sm:px-8'>
                <ul className='space-y-1'>
                    {MAIN_NAV.map((link, index) => (
                        <li key={link.href} className='overflow-hidden'>
                            <Link
                                href={link.href}
                                onClick={onClose}
                                className={cn(
                                    'flex items-baseline gap-4 py-2 font-heading text-[2.75rem] font-medium leading-none tracking-[-0.04em] transition-[translate,opacity] duration-700 ease-out-expo sm:text-6xl',
                                    open ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                                )}
                                style={{ transitionDelay: open ? `${140 + index * 60}ms` : '0ms' }}
                            >
                                <span className='font-mono text-xs font-normal tracking-normal text-beige'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                {link.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>

            <div className='shrink-0 border-t border-cream/10 px-5 pb-8 pt-6 sm:px-8'>
                <div className='grid grid-cols-2 gap-3'>
                    <ButtonLink href='/login' variant='outline-light' onClick={onClose}>
                        Log in
                    </ButtonLink>

                    <ButtonLink href='/signup' variant='light' onClick={onClose}>
                        Sign up
                    </ButtonLink>
                </div>

                <p className='mt-6 text-sm text-cream/60'>
                    Questions?{' '}
                    <a href={`mailto:${siteConfig.contact.email}`} className='text-cream underline-offset-4 hover:underline'>
                        {siteConfig.contact.email}
                    </a>
                </p>
            </div>
        </div>
    )
}
